'use client';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { useParams } from 'next/navigation';
import { FC } from 'react';
import { Icon } from '@/components/ui/icon/icon';
import { Image } from '@/components/ui/image/image';
import { NavigateButton } from '@/components/ui/navigate-button/navigate-button';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { DATE_FORMAT, formatDate, resolveDayjsLocale } from '@/utils/format-date';

interface GridCardProps {
  image: string;
  date: string;
  title: string;
  description: string;
  href: string;
  cookingTime?: string;
  locale?: string;
  titleHeadingLevel?: 2 | 3;
  /** First listing card only — avoids competing with recipe-detail LCP. */
  priority?: boolean;
}

const GridCard: FC<GridCardProps> = ({
  image,
  date,
  title,
  description,
  href,
  cookingTime,
  locale,
  titleHeadingLevel = 2,
  priority = false,
}) => {
  const { t } = useTranslation();
  const params = useParams<{ locale?: string }>();
  const TitleTag = titleHeadingLevel === 3 ? 'h3' : 'h2';

  const activeLocale = resolveDayjsLocale(locale ?? params?.locale ?? 'en');
  const effectiveDate =
    typeof date === 'string' && date.trim()
      ? date
      : href.startsWith('/blogs/')
        ? new Date().toISOString()
        : '';
  const formattedDate = effectiveDate ? formatDate(effectiveDate, activeLocale, DATE_FORMAT) : '';

  return (
    <div className="flex h-full w-full flex-col items-start gap-6 rounded-2xl bg-[#F7F7F7] pb-6">
      <div className="relative h-52 w-full shrink-0 overflow-hidden rounded-t-2xl">
        <Image
          src={image}
          alt={title}
          width={400}
          height={230}
          loading={priority ? 'eager' : 'lazy'}
          classNames={{
            wrapper: 'absolute inset-0 !max-w-none',
            img: 'h-full w-full object-cover',
          }}
        />
        {cookingTime && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 rounded-full bg-green-500 px-3 py-1.5 text-xs font-semibold text-white">
            <Icon icon={faClock} className="h-3 w-3" />
            <span>{cookingTime}</span>
          </div>
        )}
      </div>

      <div className="flex w-full flex-1 flex-col gap-6 px-3 sm:px-6">
        <div className="flex flex-col gap-2">
          <span className="text-default-600 text-sm">{formattedDate}</span>
          <TitleTag className="text-foreground text-lg font-bold sm:text-xl">{title}</TitleTag>
          <p className="text-default-700 line-clamp-2 text-sm">{description}</p>
        </div>

        <NavigateButton
          href={href}
          color="primary"
          variant="solid"
          endContent={<Icon icon={faArrowUpRight} />}
          className="mt-auto w-full justify-center sm:w-auto sm:px-6"
        >
          {t?.common.readMore}
        </NavigateButton>
      </div>
    </div>
  );
};

export default GridCard;
